import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { useState, useCallback, useEffect, useRef } from 'react';

const DEFAULT_VARIABLES = [
    { label: 'Имя менеджера', value: 'manager_name' },
    { label: 'Инста менеджера', value: 'manager_insta' },
    { label: 'Имя блогера', value: 'firstName' },
    { label: 'Подпись аккаунта', value: 'accountSignature' },
];

export default function EmailComposer({ value, onChange, disabled, placeholder, onSend, sendDisabled, onAttach, showSpintax, variables }) {
    const vars = variables || DEFAULT_VARIABLES;
    const [showVars, setShowVars] = useState(false);
    const [showLinkPopover, setShowLinkPopover] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    const varsRef = useRef(null);
    const varsBtnRef = useRef(null);
    const linkRef = useRef(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [3] },
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            }),
            Placeholder.configure({
                placeholder: placeholder || 'Написать письмо...',
            }),
        ],
        content: value || '',
        editable: !disabled,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    // Sync editable state
    useEffect(() => {
        if (editor) editor.setEditable(!disabled);
    }, [editor, disabled]);

    // Sync editor content when value changes externally (after send, or loaded from API)
    const prevValueRef = useRef(value);
    useEffect(() => {
        if (!editor) return;
        const currentHtml = editor.getHTML();
        if (value === '' && prevValueRef.current !== '') {
            editor.commands.clearContent();
        } else if (value && value !== currentHtml && value !== prevValueRef.current) {
            editor.commands.setContent(value);
        }
        prevValueRef.current = value;
    }, [editor, value]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e) => {
            if (varsRef.current && !varsRef.current.contains(e.target)) {
                setShowVars(false);
            }
            if (linkRef.current && !linkRef.current.contains(e.target)) {
                setShowLinkPopover(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const insertVariable = useCallback((varName) => {
        if (!editor) return;
        editor.chain().focus().insertContent(`{{${varName}}}`).run();
        setShowVars(false);
    }, [editor]);

    const insertSpintax = useCallback(() => {
        if (!editor) return;
        const { from, to } = editor.state.selection;
        if (from !== to) {
            const selected = editor.state.doc.textBetween(from, to);
            editor.chain().focus().deleteSelection().insertContent(`{{Random | ${selected} | }}`).run();
        } else {
            editor.chain().focus().insertContent('{{Random | вариант1 | вариант2 | вариант3}}').run();
        }
    }, [editor]);

    const handleAttach = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.onchange = (e) => {
            const file = e.target.files?.[0];
            if (file && onAttach) onAttach(file);
        };
        input.click();
    }, [onAttach]);

    const openLinkPopover = useCallback(() => {
        if (!editor) return;
        // Pre-fill from existing link or selection
        const existingHref = editor.getAttributes('link').href || '';
        const { from, to } = editor.state.selection;
        const selectedText = from !== to ? editor.state.doc.textBetween(from, to) : '';
        setLinkUrl(existingHref);
        setLinkText(selectedText);
        setShowLinkPopover(true);
    }, [editor]);

    const applyLink = useCallback(() => {
        if (!editor || !linkUrl.trim()) return;
        const href = linkUrl.trim();
        const text = linkText.trim();
        const { from, to } = editor.state.selection;
        const hasSelection = from !== to;

        if (hasSelection) {
            // Wrap selected text with link
            editor.chain().focus().setLink({ href }).run();
        } else if (text) {
            // Insert new link with custom text
            editor.chain().focus()
                .insertContent(`<a href="${href}" target="_blank">${text}</a>`)
                .run();
        } else {
            // Insert URL as link text
            editor.chain().focus()
                .insertContent(`<a href="${href}" target="_blank">${href}</a>`)
                .run();
        }
        setShowLinkPopover(false);
        setLinkUrl('');
        setLinkText('');
    }, [editor, linkUrl, linkText]);

    const removeLink = useCallback(() => {
        if (!editor) return;
        editor.chain().focus().unsetLink().run();
        setShowLinkPopover(false);
        setLinkUrl('');
        setLinkText('');
    }, [editor]);

    const handleLinkKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            applyLink();
        }
    }, [applyLink]);

    if (!editor) return null;

    const isLinkActive = editor.isActive('link');

    return (
        <div className="email-composer">
            <div className="email-composer__toolbar">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={editor.isActive('bold') ? 'is-active' : ''}
                    title="Жирный"
                >
                    <b>B</b>
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={editor.isActive('italic') ? 'is-active' : ''}
                    title="Курсив"
                >
                    <i>I</i>
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={editor.isActive('underline') ? 'is-active' : ''}
                    title="Подчёркнутый"
                >
                    <u>U</u>
                </button>
                <span className="email-composer__separator" />
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
                    title="Заголовок"
                >
                    H
                </button>
                <span className="email-composer__separator" />
                {/* Link button */}
                <div className="email-composer__link-dropdown" ref={linkRef}>
                    <button
                        type="button"
                        onClick={openLinkPopover}
                        className={isLinkActive || showLinkPopover ? 'is-active' : ''}
                        title="Вставить ссылку"
                    >
                        🔗
                    </button>
                    {showLinkPopover && (
                        <div className="email-composer__link-popover">
                            <input
                                type="url"
                                className="email-composer__link-input"
                                placeholder="https://..."
                                value={linkUrl}
                                onChange={e => setLinkUrl(e.target.value)}
                                onKeyDown={handleLinkKeyDown}
                                autoFocus
                            />
                            <input
                                type="text"
                                className="email-composer__link-input"
                                placeholder="Текст ссылки (необязательно)"
                                value={linkText}
                                onChange={e => setLinkText(e.target.value)}
                                onKeyDown={handleLinkKeyDown}
                            />
                            <div className="email-composer__link-actions">
                                <button type="button" className="email-composer__link-btn" onClick={applyLink} disabled={!linkUrl.trim()}>
                                    Вставить
                                </button>
                                {isLinkActive && (
                                    <button type="button" className="email-composer__link-btn email-composer__link-btn--remove" onClick={removeLink}>
                                        Убрать
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <span className="email-composer__separator" />
                <div className="email-composer__variables-dropdown" ref={varsRef}>
                    <button
                        ref={varsBtnRef}
                        type="button"
                        onClick={() => setShowVars(!showVars)}
                        className={showVars ? 'is-active' : ''}
                        title="Вставить переменную"
                    >
                        {'{{ }}'}
                    </button>
                    {showVars && varsBtnRef.current && (() => {
                        const rect = varsBtnRef.current.getBoundingClientRect();
                        return (
                            <div className="email-composer__variables-menu" style={{
                                position: 'fixed',
                                left: rect.left,
                                bottom: window.innerHeight - rect.top + 4,
                                top: 'auto'
                            }}>
                                {vars.map(v => (
                                    <button
                                        key={v.value}
                                        type="button"
                                        onClick={() => insertVariable(v.value)}
                                    >
                                        {v.label} <span className="email-composer__var-code">{`{{${v.value}}}`}</span>
                                    </button>
                                ))}
                            </div>
                        );
                    })()}
                </div>
                {showSpintax && (
                    <>
                        <span className="email-composer__separator" />
                        <button
                            type="button"
                            onClick={insertSpintax}
                            title="Вставить spintax {вариант1|вариант2}"
                        >
                            {'{ | }'}
                        </button>
                    </>
                )}
                <div className="email-composer__spacer" />
                {onAttach && (
                    <button type="button" onClick={handleAttach} title="Прикрепить файл">
                        📎
                    </button>
                )}
                {onSend && (
                    <button
                        type="button"
                        className="email-composer__send-btn"
                        onClick={onSend}
                        disabled={sendDisabled}
                        title="Отправить"
                    >
                        ▶
                    </button>
                )}
            </div>
            <EditorContent editor={editor} className="email-composer__body" />
        </div>
    );
}
